import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { InputsService } from '@app/inputs/services/inputs.service';
import { ModifyOptionsFnType } from '@app/shared/components/menu/menu.component';
import {
  SourceUserType,
  UserRecommendationHeaderItem,
} from '@app/shared/components/user-recommendation-header/user-recommendation-header.component';
import { DgError } from '@app/shared/models/dg-error';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { ContentActionPipe } from '@app/shared/pipes/content-action.pipe';
import { DisplayTypePipe } from '@app/shared/pipes/display-type.pipe';
import { AuthService } from '@app/shared/services/auth.service';
import { PreloadService } from '@app/shared/services/preload.service';
import { camelCaseKeys } from '@app/shared/utils/property';
import { TranslateService } from '@ngx-translate/core';
import { ConnectableObservable, Observable } from 'rxjs';
import { catchError, map, publish, tap } from 'rxjs/operators';
import {
  ResourceSectionType,
  ResourceSuggestionDetails,
  ResourceSuggestionWithDetails,
  SuggestedResources,
  SuggestedResourcesResponse,
} from '../learner-home-api.model';
import { SectionTrackingProperties } from './learner-home-tracker.service';

@Injectable()
export class SuggestedResourcesService {
  public i18n = this.translateService.instant([
    'Core_AreYouSure',
    'Core_RemoveSuccess',
    'QueueCtrl_RemoveSavedItems',
    'Core_RemoveSuccess',
    'Core_Dismiss',
  ]);

  constructor(
    private authService: AuthService,
    private http: NgxHttpClient,
    private translateService: TranslateService,
    private InputsService: InputsService,
    private contentActionPipe: ContentActionPipe,
    private displayTypePipe: DisplayTypePipe,
    private datePipe: DatePipe,
    private preloadService: PreloadService
  ) {}

  public dismissItem(
    resource: any,
    suggestion: ResourceSuggestionWithDetails,
    sectionTrackingProperties: SectionTrackingProperties,
    element?: HTMLElement
  ) {
    return this.InputsService.dismissSuggestedInput(
      resource,
      suggestion,
      sectionTrackingProperties,
      element
    );
  }

  /**
   * Load new learner home sections with suggestions.
   *
   * The method takes either a section name when loading suggestions
   * for a single section and alternatively a section count and section skip args to load multiple.
   */
  public getSuggestedResources(
    params:
      | {
          section: ResourceSectionType;
          take?: number;
          skip?: number;
          useResourceImages?: boolean;
        }
      | {
          sections: number;
          sectionsToSkip: number;
          take?: number;
          skip?: number;
          useResourceImages?: boolean;
        }
  ): Observable<SuggestedResources> {
    const resources$ =
      this.tryGetPreloadedResources(params) ??
      this.http.get<SuggestedResourcesResponse>(
        '/inputs/GetSuggestedResources',
        {
          params,
        }
      );
    return resources$ // This is just here to cause a line break
      .pipe(
        // Combine the references with the statistics that are on the suggestion object
        // and parse suggestionDetails string into an object
        map((result) =>
          result.map((section) => ({
            ...section,
            resourceSuggestions: section.resourceSuggestions.map(
              ({ reference, statistics, ...suggestion }) => {
                const suggestionDetails: ResourceSuggestionDetails =
                  camelCaseKeys(
                    JSON.parse(suggestion.suggestionDetails || '{}')
                  );

                // If we have a sourceUser in suggestionDetails, use that in the topUser
                // in statistics so we always highlight a user that the user actually
                // follows.
                if (
                  suggestionDetails.sourceUser?.userType ===
                    SourceUserType.FollowedUser &&
                  statistics?.topUser
                ) {
                  statistics = {
                    ...statistics,
                    topUser: {
                      vanityUrl: suggestionDetails.sourceUser.profileUrl,
                      ...suggestionDetails.sourceUser,
                    },
                  };
                } else if (
                  suggestionDetails.sourceUser?.userType ===
                    SourceUserType.FollowedUser &&
                  !statistics
                ) {
                  // Synthesize statistics for non-content content like pathways so we can show who followed those
                  statistics = {
                    userCount: reference.userCount,
                    topUser: {
                      vanityUrl: suggestionDetails.sourceUser.profileUrl,
                      ...suggestionDetails.sourceUser,
                    },
                    inputId: reference.resourceId,
                    inputType: reference.resourceType,
                    // the rest are not important to pass
                    topRecommender: undefined,
                    recommendationCount: undefined,
                    commentCount: undefined,
                    likeCount: undefined,
                    dislikeCount: undefined,
                    viewCount: undefined,
                  };
                }

                // If a suggestion is a recommendation, create a recommendation object.
                const recommendation = suggestionDetails?.recommendationType
                  ? this.getRecommendationData(
                      suggestionDetails,
                      suggestion.referenceType
                    )
                  : null;

                return {
                  ...suggestion,
                  suggestionDetails,
                  action: suggestionDetails.action,
                  isAssignment: this.isAssignment(suggestionDetails),
                  recommendation,
                  reference: {
                    ...reference,
                    statistics,
                  },
                  statistics,
                };
              }
            ),
          }))
        ),
        catchError((error) => {
          throw new DgError(
            this.translateService.instant('InputsSvc_GetSuggestedError'),
            error
          );
        })
      );
  }

  /** Create the recommendation object passed to the @UserRecommendationHeader component */
  public getRecommendationData(
    suggestionDetails: ResourceSuggestionDetails,
    referenceType: string
  ): UserRecommendationHeaderItem {
    const group = suggestionDetails.group
      ? {
          groupId: suggestionDetails.group.id,
          groupName: suggestionDetails.group.name,
        }
      : null;
    const creator = suggestionDetails.sourceUser
      ? {
          vanityUrl: suggestionDetails.sourceUser?.profileUrl,
          name: suggestionDetails.sourceUser?.name,
          picture: suggestionDetails.sourceUser?.picture,
          isEngaged: suggestionDetails.sourceUser.isEngaged,
          isMentoring: suggestionDetails.sourceUser.isMentoring,
        }
      : null;

    return {
      creator,
      group,
      contentAction: this.contentActionPipe
        .transform(referenceType)
        .toLowerCase(),
      contentType: this.displayTypePipe.transform(referenceType).toLowerCase(),
      recommendationType: suggestionDetails.recommendationType,
      dateDue: suggestionDetails.dueDate,
      displayDueDate: suggestionDetails.dueDate
        ? this.datePipe.transform(suggestionDetails.dueDate, 'mediumDate')
        : null,
      isAssignment: this.isAssignment(suggestionDetails),
    };
  }

  public getModifyOptionsFn({
    onRemove,
    suggestion,
    sectionTrackingProperties,
  }: {
    onRemove?: (suggestion: ResourceSuggestionWithDetails) => void;
    suggestion: ResourceSuggestionWithDetails;
    sectionTrackingProperties: SectionTrackingProperties;
  }): ModifyOptionsFnType<LearningResourceViewModel> {
    // only return a menu config when using the new content cards and the suggestion is an input, pathway, or target
    if (
      !suggestion.reference.inputType &&
      suggestion.reference.resourceType !== 'Pathway' &&
      suggestion.reference.resourceType !== 'Target' &&
      suggestion.reference.resourceType !== 'User'
    ) {
      return () => [];
    }

    return (content, defaultMenuConfig) => [
      ...defaultMenuConfig,
      {
        title: this.i18n.Core_Dismiss,
        isHidden: () => {
          return (
            content.completionInfo?.isCompleted ||
            this.isRequired(suggestion.suggestionDetails) ||
            content.completionInfo?.externalCompletionOnly
          );
        },
        defaultAction: (event) => {
          this.dismissItem(
            { isModal: false },
            suggestion,
            sectionTrackingProperties,
            event.target as HTMLElement
          ).subscribe(() => {
            onRemove(suggestion);
          });
        },
      },
    ];
  }

  public toggleFeatureGroup(groupId: number) {
    const isMember = !!(this.authService.authUser as any)?.viewerGroups?.find(
      (group) => group.groupId === groupId
    );

    const observable = this.http
      .post<{}>(`/groups/${isMember ? 'leave' : 'join'}`, {
        GroupId: groupId,
      })
      .pipe(
        tap(() => {
          // Ensure the auth user is fetched fresh after the reload
          this.authService.clearAuth();
          window.location.reload();
        }),
        publish()
      ) as ConnectableObservable<any>;

    observable.connect();
    return observable;
  }

  /** Try to find already preloaded resources for the first few sections */
  private tryGetPreloadedResources(params: {
    section?: any;
    sections?: number;
    sectionsToSkip?: number;
  }): Observable<SuggestedResourcesResponse> {
    if (params.sectionsToSkip + params.sections <= 4) {
      return this.preloadService
        .get<SuggestedResourcesResponse>(
          `/api/inputs/GetSuggestedResources?sections=4&sectionsToSkip=0&useResourceImages=true&dg-casing=camel`
        )
        ?.pipe(
          map((results) =>
            results.slice(
              params.sectionsToSkip,
              params.sectionsToSkip + params.sections
            )
          )
        );
    }
  }

  private isAssignment(suggestionDetails) {
    return (
      this.isRequired(suggestionDetails) ||
      suggestionDetails?.recommendationType === 'AssignedLearning'
    );
  }

  private isRequired(suggestionDetails) {
    return suggestionDetails?.recommendationType === 'RequiredLearning';
  }
}
